import cx from "classnames";
import { ChannelPreview } from "components/ChannelPreview/ChannelPreview";
import { ChannelListFilter } from "components/ChannelSearch/ChannelListFilter";
import DashboardContainer from "components/dashboard/DashboardContainer";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { EmptyChannelList } from "components/EmptyChannelList";
import * as CHANNEL_TYPES from "constants/channelType";
import { ARCHIVED_CHAT_TAB } from "constants/chatTab";
import { AI_CHANNEL, ANNOUNCEMENTS_CHANNEL } from "constants/chatType";
import { useEntityChatFilter } from "contexts/EntityChatFilterContext";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import React, { ComponentProps, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { setCartInChat } from "store/slices/cartInChat";
import { setToOpenChatId } from "store/slices/chat";
import { setShowAIChannels } from "store/slices/chatSearch";
import { RootState } from "store/store";
import { Channel, ChannelFilters, ChannelSort } from "stream-chat";
import {
  InfiniteScroll,
  useChannelStateContext,
  useChatContext,
} from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { ChannelFilterOrSearch } from "./ChannelFilterOrSearch";
import { ChatFilterPills } from "./ChatFilterPills";
import { SelectedChannels } from "./SelectedChannelContext";
import { SlackIntegration } from "components/SlackIntegration";
import { CHAT_ARCHIVED_TAB_CLICKED } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import authContext from "jwt_context&axios/authContext";
import { useFilteredChannelIds } from "hooks/useFilteredChannelIds";
import { CustomisedChannelLists } from "./CustomisedChannelLists";

export const ChannelList = ({
  filters: propFilters,
  unreplied = false,
  broadcast = false,
  onSelect,
  channelId,
  hideTabBar,
  hideFilters,
  sortByNewest: defaultNewestSort = true,
  isMyMessages,
  hideHeaders = false,
  hideChannelCheckboxSelect,
  showEntityFilters,
}: {
  filters?: ChannelFilters<DefaultStreamChatGenerics>;
  unreplied?: boolean;
  broadcast?: boolean;
  onSelect?: (channelId: string, messageId?: string) => void;
  channelId?: string;
  hideTabBar?: boolean;
  sortByNewest?: boolean;
  isMyMessages?: boolean;
  hideHeaders?: boolean;
  hideChannelCheckboxSelect?: boolean;
  showEntityFilters?: boolean;
} & Pick<ComponentProps<typeof ChannelListFilter>, "hideFilters">) => {
  const { isAdmin, isCpa, isCustomer, isUserTypeForeignCA, isForeignCA } =
    useRoleBasedView();
  const { client } = useChatContext();
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const isFCA = isUserTypeForeignCA || isForeignCA;
  const { authtoken } = useContext(authContext);
  const FCA_TEAM_CHANNEL_ID = authtoken?.service_team_channel_url;
  const [channelType, setChannelType] = React.useState<string>(
    isFCA ? "ALL" : "PUBLIC"
  );
  const searchValue = useSelector((state: RootState) => state.chatSearch.value);
  const isUnreadFilterActive = useSelector(
    (state: RootState) => state.chatSearch.unread
  );
  const { secondary_channel_url } = useCurrentGroup();
  const { channel: currentChannel } = useChatContext();
  const {
    showMentionedMessages,
    sortByNewest,
    showUnrepliedMessages,
    showAIChannels,
  } = useSelector((state: RootState) => state.chatSearch);
  const {
    selectedEntityIds,
    selectedSeasons,
    isUnreadOnly,
    isPendingActionOnly,
  } = useEntityChatFilter();
  const { channelIds, isResolving, hasFailed } = useFilteredChannelIds(
    channelType === ARCHIVED_CHAT_TAB
  );

  const query = useQuery();
  const otherRadioValue = query.get("other") || null;
  const isAIFromQuery = query.get("ai") === "true";

  const isStarred = otherRadioValue === "STARRED_CHAT";

  let filters = propFilters || {
    type: "messaging",
    members: { $in: [client.userID!] },
  };

  filters = {
    ...filters,
    frozen: false,
  };

  if (channelType === "CLOSED" && !broadcast) {
    filters = {
      ...filters,
      frozen: true,
    };
  }

  if (channelType === "PRIVATE" && isCpa && !broadcast) {
    filters = {
      ...filters,
      custom_type: { $eq: CHANNEL_TYPES.PRIVATE_CHANNEL_CUSTOM_TYPE },
    };
  }

  if (channelType === "PUBLIC" && isCpa && !broadcast) {
    filters = {
      ...filters,
      custom_type: {
        $in: Object.values(CHANNEL_TYPES).filter(
          (key) =>
            ![
              CHANNEL_TYPES.PRIVATE_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.ANNOUNCEMENTS_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.AI_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.KB_SEARCH_CHANNEL_CUSTOM_TYPE,
            ].includes(key)
        ),
      },
    };
  }

  if (channelType === "PUBLIC" && !isCpa && !broadcast) {
    filters = {
      ...filters,
      custom_type: {
        $in: Object.values(CHANNEL_TYPES).filter(
          (key) =>
            ![
              CHANNEL_TYPES.AI_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.KB_SEARCH_CHANNEL_CUSTOM_TYPE,
            ].includes(key)
        ),
      },
    };
  }

  if (channelType === "ALL" && !broadcast) {
    filters = {
      ...filters,
      custom_type: {
        $in: Object.values(CHANNEL_TYPES).filter(
          (key) =>
            ![
              CHANNEL_TYPES.AI_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.KB_SEARCH_CHANNEL_CUSTOM_TYPE,
            ].includes(key)
        ),
      },
    };
  }

  if (showAIChannels || isAIFromQuery) {
    filters = {
      ...filters,
      custom_type: { $eq: CHANNEL_TYPES.AI_CHANNEL_CUSTOM_TYPE },
    };
  }

  const onChannelUpdated = async (
    setChannels: React.Dispatch<
      React.SetStateAction<Array<Channel<DefaultStreamChatGenerics>>>
    >
  ) => {
    setChannels((channels) => {
      if (unreplied) {
        return channels.filter((channel) => channel?.data?.needs_reply);
      }

      if (broadcast) {
        return channels.filter(
          (channel) =>
            channel?.data?.custom_type !== ANNOUNCEMENTS_CHANNEL &&
            channel?.data?.custom_type !== AI_CHANNEL
        );
      }

      let customActiveChannelObject = channels.find(
        (chan) => chan?.id === customActiveChannel
      );

      let newChannels = [
        currentChannel,
        ...channels.filter(
          (channel) =>
            channel?.id !== customActiveChannel &&
            channel?.id !== currentChannel?.id
        ),
      ];

      if (
        customActiveChannelObject &&
        customActiveChannelObject?.id !== currentChannel?.id
      ) {
        newChannels = [customActiveChannelObject, ...newChannels];
      }

      return newChannels as Channel<DefaultStreamChatGenerics>[];
    });
  };

  const { groupId: groupIdFromParams } = useParams<{ groupId?: string }>();
  const isCrmView = Boolean(groupIdFromParams);

  const channelSortKey =
    (isAdmin || isCpa) && !isCrmView
      ? "last_non_broadcast_message_at"
      : "last_message_at";

  let sort: ChannelSort = {};
  if (sortByNewest === null) {
    sort[channelSortKey] = defaultNewestSort ? -1 : 1;
  } else {
    sort[channelSortKey] = sortByNewest ? -1 : 1;
  }

  if (isUnreadFilterActive) {
    sort = {
      has_unread: -1,
    };
  }

  if (isStarred) {
    filters = {
      ...filters,
      is_vip: true,
    };
  }

  if (channelIds) {
    filters = {
      ...filters,
      cid: { $in: channelIds.map((channelId) => `messaging:${channelId}`) },
    } as ChannelFilters<DefaultStreamChatGenerics>;
  }

  if (isUnreadOnly || isPendingActionOnly) {
    sort = { has_unread: -1 };
  }

  let customActiveChannel: string | undefined;

  if (isAdmin || isCustomer) {
    customActiveChannel =
      searchValue || isUnreadFilterActive ? undefined : secondary_channel_url;
  } else if (isFCA) {
    customActiveChannel =
      searchValue || isUnreadFilterActive ? undefined : FCA_TEAM_CHANNEL_ID;
  }
  const onSelectCallback = (channelId?: string, messageId?: string) => {
    if (onSelect && channelId) {
      onSelect(channelId, messageId);
    }
    if (channelId) {
      dispatch(setToOpenChatId(channelId)); // Ensure channelId is defined
    }
  };

  const hideFiltersMemo = React.useMemo(() => {
    return hideFilters;
  }, [JSON.stringify(hideFilters)]);

  const handleChannelTypeChange = (value: string) => {
    if (!value) {
      return;
    }

    if (value === "CLOSED") {
      trackEvent(CHAT_ARCHIVED_TAB_CLICKED);
    }

    setChannelType(value);
  };

  return (
    <SelectedChannels>
      <DashboardContainer className="t-h-full t-transition-all">
        {!hideHeaders && (
          <DashboardContainer.Header>
            <div className="t-py-4 t-px-4 t-flex t-flex-col t-gap-4">
              {/* Stands in for the chat page tabs, which are desktop only */}
              <ChatFilterPills className="md:t-hidden" />
              {!hideTabBar &&
                (isCpa && !isFCA && !broadcast ? (
                  <ToggleGroup
                    value={channelType}
                    onValueChange={(value) => {
                      handleChannelTypeChange(value);
                      if (value && value !== "ALL") {
                        dispatch(setShowAIChannels(false));
                      }
                    }}
                  >
                    <ToggleGroupItem value="ALL">All</ToggleGroupItem>
                    <ToggleGroupItem value="PUBLIC">Public</ToggleGroupItem>
                    <ToggleGroupItem value="PRIVATE">Private</ToggleGroupItem>
                    <ToggleGroupItem value="CLOSED">Archived</ToggleGroupItem>
                  </ToggleGroup>
                ) : (
                  <ToggleGroup
                    value={channelType}
                    onValueChange={handleChannelTypeChange}
                  >
                    <ToggleGroupItem value="PUBLIC">Open</ToggleGroupItem>
                    <ToggleGroupItem value="CLOSED">Archived</ToggleGroupItem>
                  </ToggleGroup>
                ))}
              <ChannelFilterOrSearch
                unreplied={unreplied}
                hideFilters={hideFiltersMemo}
                defaultNewestSort={defaultNewestSort}
                isMyMessages={isMyMessages}
                showEntityFilters={showEntityFilters}
              />
            </div>
          </DashboardContainer.Header>
        )}
        <DashboardContainer.Content
          className={cx({
            "hide-scrollbar": searchValue || unreplied,
          })}
        >
          <CustomisedChannelLists
            isResolvingChannels={isResolving}
            hasFilterError={hasFailed}
            searchValue={searchValue}
            onSelectCallback={onSelectCallback}
            filters={filters}
            sort={sort}
            unreplied={unreplied}
            customActiveChannel={customActiveChannel}
            onChannelUpdated={onChannelUpdated}
            hideChannelCheckboxSelect={hideChannelCheckboxSelect}
          />
        </DashboardContainer.Content>
      </DashboardContainer>
    </SelectedChannels>
  );
};
